<template>
  <div
    class="inline-flex items-center text-rent-risk-blue-400 hover:text-rent-risk-blue-500"
    ref="triggerRef"
    tabindex="0"
    role="button"
    :aria-label="title || 'More information'"
    :aria-expanded="trigger === 'click' ? isVisible : undefined"
    @mouseenter="handleTriggerEnter"
    @mouseleave="handleTriggerLeave"
    @focus="handleTriggerEnter"
    @blur="handleTriggerBlur"
    @click="handleTriggerClick"
    @keydown="handleTriggerKeydown"
  >
    <slot name="trigger">
      <font-awesome-icon :icon="faCircleInfo" class="ml-1 h-4 w-4 cursor-pointer" />
    </slot>
  </div>

  <Teleport to="body">
    <transition name="fade">
      <div
        v-if="isVisible"
        ref="tooltipRef"
        class="fixed z-[100] w-80 max-w-[min(100vw-2rem,22rem)] rounded shadow-lg"
        :style="tooltipStyle"
        @mouseenter="handleTooltipEnter"
        @mouseleave="handleTooltipLeave"
      >
        <div class="bg-white">
          <slot>
            <div class="bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-t">
              {{ title }}
            </div>
            <div class="p-4 text-sm text-gray-600 border border-gray-300 border-t-0" v-html="description"></div>
          </slot>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons'
import { computeTooltipPosition } from '@/Shared/tooltipPosition'

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  // 'hover' (default) shows on mouseenter/focus; 'click' toggles on click and
  // closes on outside click or Escape — hover/focus handlers are inert.
  trigger: {
    type: String,
    default: 'hover',
    validator: (value) => ['hover', 'click'].includes(value),
  },
})

const triggerRef = ref(null)
const tooltipRef = ref(null)
const isVisible = ref(false)
const tooltipPosition = ref({ top: 0, left: 0 })

const tooltipStyle = computed(() => ({
  top: `${tooltipPosition.value.top}px`,
  left: `${tooltipPosition.value.left}px`,
}))

const updatePosition = async () => {
  await nextTick()
  const trigger = triggerRef.value
  const tooltip = tooltipRef.value
  if (!trigger || !tooltip) return

  tooltipPosition.value = computeTooltipPosition({
    triggerRect: trigger.getBoundingClientRect(),
    tooltipRect: tooltip.getBoundingClientRect(),
    viewportWidth: document.documentElement.clientWidth,
  })
}

let hideTimeout = null

const clearPendingHide = () => {
  if (hideTimeout !== null) {
    clearTimeout(hideTimeout)
    hideTimeout = null
  }
}

const handleTriggerEnter = () => {
  if (props.trigger === 'click') return
  clearPendingHide()
  isVisible.value = true
  updatePosition()
}

/** Delay so the pointer can move from the (i) into the teleported panel (e.g. to click a link). */
const handleTriggerLeave = () => {
  if (props.trigger === 'click') return
  hideTimeout = window.setTimeout(() => {
    isVisible.value = false
    hideTimeout = null
  }, 200)
}

const handleTooltipEnter = () => {
  if (props.trigger === 'click') return
  clearPendingHide()
}

const handleTooltipLeave = () => {
  if (props.trigger === 'click') return
  isVisible.value = false
}

const handleTriggerBlur = () => {
  if (props.trigger === 'click') return
  clearPendingHide()
  isVisible.value = false
}

const handleTriggerClick = () => {
  if (props.trigger !== 'click') return
  isVisible.value = !isVisible.value
  if (isVisible.value) {
    updatePosition()
  }
}

/**
 * Keyboard activation for click mode only. Hover-mode tooltips must fall through
 * untouched — swallowing Space there would break page scrolling on the dozen pages
 * that render the focusable (i) icon.
 */
const handleTriggerKeydown = (event) => {
  if (props.trigger !== 'click') return
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  handleTriggerClick()
}

const handleOutsidePointer = (event) => {
  if (props.trigger !== 'click' || !isVisible.value) return
  const insideTrigger = triggerRef.value?.contains(event.target)
  const insideTooltip = tooltipRef.value?.contains(event.target)
  if (!insideTrigger && !insideTooltip) {
    isVisible.value = false
  }
}

const handleEscape = (event) => {
  if (props.trigger !== 'click' || !isVisible.value) return
  if (event.key === 'Escape') {
    isVisible.value = false
  }
}

const handleScrollOrResize = () => {
  if (isVisible.value) {
    updatePosition()
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScrollOrResize, true)
  window.addEventListener('resize', handleScrollOrResize)

  // Only click mode needs document-level dismissal. Hover tooltips are rendered
  // once per feature row on the pricing table, so registering these for them too
  // would add dozens of listeners that can never fire.
  if (props.trigger === 'click') {
    document.addEventListener('pointerdown', handleOutsidePointer)
    document.addEventListener('keydown', handleEscape)
  }
})

onUnmounted(() => {
  clearPendingHide()
  window.removeEventListener('scroll', handleScrollOrResize, true)
  window.removeEventListener('resize', handleScrollOrResize)
  document.removeEventListener('pointerdown', handleOutsidePointer)
  document.removeEventListener('keydown', handleEscape)
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
